import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSprintData } from './hooks/useSprintData'
import { useIssueProcessing } from './hooks/useIssueProcessing'
import { useFilters } from './hooks/useFilters'
import { PageHeader } from '../../../Elements/PageHeader'
import { Loading, Error } from '../../../Elements/Indicators'
import { CommitmentBar } from './components/CommitmentBar'
import { IssueGrid } from './components/IssueGrid'
import { TimelineView } from './components/TimelineView'
import clsx from 'clsx'
import type { RouteParams } from './types'

const SprintUserAnalysis: React.FC = () => {
  const { projKey, boardId, sprintId, userId } = useParams<RouteParams>()
  const { sprint, analysis, userAnalysis, user, addedByLookup, isLoading, isLoadingSprint, error, errorSprint, userStory, isLoadingUserStory }
= useSprintData()
  const { displayedIssues, issueTitleMap, uniqEpics, uniqStatuses, issueTypes, bugCategories, ticketCategories, changeCategories, removedIssues }
= useIssueProcessing({ analysis, userAnalysis, userId })
  const { filters, setFilters, appliedIssues } = useFilters(displayedIssues)
  const [openPanel, setOpenPanel] = useState<null | 'bugs' | 'tickets' | 'changes'>(null)
  const [view, setView] = useState<'cards' | 'timeline'>('cards')

  if (isLoading || isLoadingSprint) return <Loading message={`Loading sprint ${sprintId} analysis.`} />
  if (error || errorSprint) return <Error title={'Error Loading Sprint Analysis'} message={((errorSprint || error) as Error)?.message || 'An unknown error occurred'} />
  if (!sprint || !analysis || !userId) return <Error title={'Error Loading Sprint Analysis'} message={'Missing data'} />
  if (!userAnalysis || !user) {
    const availableAnalysisIds = analysis.byUser.map(u => `${u.id} (${u.name})`).join(', ')
    const availableSprintUserIds = sprint.users.map(u => `${u.accountId} (${u.displayName})`).join(', ')
    return <Error title={'Error Loading Sprint Analysis'} message={`User with ID ${userId} not found. Available analysis users: ${availableAnalysisIds}. Available sprint users: ${availableSprintUserIds}.`} />
  }

  const headerConfig = {
    pageTitle: `${user.displayName} in ${sprint.name}`,
    backLink: { label: 'Back', to: '/projects' },
    breadcrumb: [
      { label: 'Projects', to: '/projects' },
      { label: 'Project Details', to: `/projects/${projKey}` },
      { label: `Sprint ${sprint.id} Analysis`, to: `/projects/${projKey}/boards/${boardId}/sprints/${sprintId}/analysis` },
      { label: 'User Analysis' }
    ],
    rightComponent: (
      <div className='flex items-center gap-3'>
        <div className='flex rounded-full border border-gray-200 bg-gray-100 p-0.5'>
          <button onClick={() => setView('cards')} className={clsx('rounded-full px-4 py-1.5 text-sm font-medium transition-colors', view === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>Tickets</button>
          <button onClick={() => setView('timeline')} className={clsx('rounded-full px-4 py-1.5 text-sm font-medium transition-colors', view === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>Timeline</button>
        </div>
        {user.avatar && <img className='h-10 w-10 rounded-full' src={user.avatar} alt='' />}
      </div>
    )
  }

  return (
    <>
      {headerConfig && <PageHeader config={headerConfig} />}
      <main className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <CommitmentBar userAnalysis={userAnalysis} sprintId={sprintId ?? ''} openPanel={openPanel} setOpenPanel={setOpenPanel} addedByLookup={addedByLookup} isLoadingAddedBy={false} bugCategories={bugCategories} ticketCategories={ticketCategories} changeCategories={changeCategories} />
        {view === 'timeline' && (
          <div>
            {isLoadingUserStory ? <div className='flex items-center justify-center py-8'><Loading message='Loading timeline...' /></div>
            : userStory ? <TimelineView userStory={userStory} issueTitleMap={issueTitleMap} changes={userAnalysis.changes} />
            : <div className='py-4 text-center text-gray-500'>No timeline data available</div>}
          </div>
        )}
        {view === 'cards' && (
          <IssueGrid displayedIssues={appliedIssues} filters={filters} setFilters={setFilters} filterOptions={{ issueTypes, uniqEpics, uniqStatuses }} sprintId={sprintId ?? ''} addedByLookup={addedByLookup} isLoadingAddedBy={false} removedIssues={removedIssues} />
        )}
      </main>
    </>
  )
}

export default SprintUserAnalysis